/**
 * Color-coded role badge component for team members.
 * Colors per CONTEXT.md: OWNER (gold/amber), ADMIN (blue), AGENT (green), VIEWER (gray)
 */

import { useTranslation } from 'react-i18next';
import type { TeamRole } from '../../types/company';

interface RoleBadgeProps {
  role: TeamRole;
  size?: 'sm' | 'md';
}

const ROLE_COLORS: Record<TeamRole, string> = {
  OWNER: 'bg-amber-100 text-amber-800 border-amber-200',
  ADMIN: 'bg-blue-100 text-blue-800 border-blue-200',
  AGENT: 'bg-green-100 text-green-800 border-green-200',
  VIEWER: 'bg-gray-100 text-gray-600 border-gray-200',
};

const ROLE_KEYS: Record<TeamRole, string> = {
  OWNER: 'roleOwner',
  ADMIN: 'roleAdmin',
  AGENT: 'roleAgent',
  VIEWER: 'roleViewer',
};

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export default function RoleBadge({ role, size = 'sm' }: RoleBadgeProps) {
  const { t } = useTranslation('team');

  const colorClasses = ROLE_COLORS[role];
  const sizeClasses = SIZE_CLASSES[size];
  const roleLabel = t(ROLE_KEYS[role]);

  return (
    <span
      className={`
        inline-flex items-center rounded-full border font-medium
        ${colorClasses}
        ${sizeClasses}
      `}
    >
      {roleLabel}
    </span>
  );
}
